// ModelFilesDrawer — a LOCAL product's documents face in a SlideOver (the one
// place the library sits in a fourth container — FRONTEND-PATTERNS §Files).
// Everything on a model is FILED (the 2026-09-05 ruling): the upload asks only
// "Where?" (the model's folders — natureLocked), a row's acts are the paper's
// (download · rename · move to a folder · replace · delete), and the small
// recycle bin sits at the end with Restore. The writer is the catalog manager
// (asset.models:manage — the anchor's own librarian, ADR-068 §5).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import type { AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import type { LibraryFile as WireFile } from '@ems-app/schemas/files/responses';
import { CLASS_FAMILIES, CLASS_MAX_BYTES, FAMILY_MAX_BYTES, FOLDERS_BY_CLASS, SNIFFED_TYPES, type ContentFamily } from '@ems-app/schemas/files/vocabulary';
import { ActionMenu } from '../../components/patterns/ActionMenu.js';
import { MoveFileDialog, type MoveRoad, type MoveTarget } from '../../components/patterns/MoveFileDialog.js';
import { RenameFileDialog } from '../../components/patterns/RenameFileDialog.js';
import { ReplaceFileDialog } from '../../components/patterns/ReplaceFileDialog.js';
import { UploadFilesDialog } from '../../components/patterns/UploadFilesDialog.js';
import { FileRow, displayName, shortFileName, type LibraryFile } from '../../components/patterns/filesDisplay.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { SlideOver } from '../../components/ui/SlideOver.js';
import { icons } from '../../components/ui/icons.js';
import { useToast } from '../../components/ui/Toast.js';
import { apiErrorCode } from '../../lib/apiError.js';
import { ModelFileSurface } from './ModelFileSurface.js';
import {
  downloadFile,
  uploadFile,
  useBin,
  useDeleteFile,
  useFilesOptions,
  useMoveFile,
  useRenameFile,
  useRestoreFile,
  useShelf,
} from '../../hooks/useFiles.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

function familyHint(file: File): ContentFamily {
  const t = file.type;
  if (t.startsWith('image/')) return 'image';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t === 'application/pdf') return 'pdf';
  return SNIFFED_TYPES[t] ?? 'document';
}
const capFor = (file: File): number => Math.min(CLASS_MAX_BYTES.model, FAMILY_MAX_BYTES[familyHint(file)]);
const ACCEPT = Object.entries(SNIFFED_TYPES)
  .filter(([, family]) => CLASS_FAMILIES.model.includes(family))
  .map(([mime]) => mime)
  .join(',');
const MODEL_FOLDERS = FOLDERS_BY_CLASS.model ?? [];

export function ModelFilesDrawer({ projectId, model, open, onClose }: { projectId: string; model: AssetModelProjectItem; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('catalog');
  const { t: tf } = useTranslation('files');
  const toast = useToast();
  const qc = useQueryClient();
  const anchor = `model:${model.id}`;

  const shelf = useShelf(projectId, { anchor });
  const bin = useBin(projectId, anchor);
  const options = useFilesOptions(projectId, anchor);
  const toView = (f: WireFile): LibraryFile => ({ ...f, thumb: null, fresh: false });
  const files = useMemo(() => (shelf.data?.documents ?? []).map(toView), [shelf.data]);
  const binItems = useMemo(() => (bin.data?.items ?? []).map(toView), [bin.data]);
  const binGraceDays = options.data?.bin_grace_days ?? 30;

  const rename = useRenameFile(projectId);
  const move = useMoveFile(projectId);
  const del = useDeleteFile(projectId);
  const restore = useRestoreFile(projectId);
  const fail = useRefusalToast('model-files');

  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<LibraryFile | null>(null);
  const [moving, setMoving] = useState<LibraryFile | null>(null);
  const [replacing, setReplacing] = useState<LibraryFile | null>(null);
  const [toDelete, setToDelete] = useState<LibraryFile | null>(null);

  const folderLabel = (f: string) => tf(`folders.${f}`, { defaultValue: f });

  return (
    <>
      <SlideOver open={open} onClose={onClose} title={t('docs.manageTitle', { name: model.name })}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-[var(--color-text-secondary)]">{t('docs.manageHint')}</p>
            <Button variant="secondary" onClick={() => setUploading(true)}>
              <icons.upload size={16} aria-hidden />
              {tf('actions.upload')}
            </Button>
          </div>
          {files.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)]">{t('peek.docsNone')}</p>
          ) : (
            <div className="-mx-3 flex flex-col divide-y divide-[var(--color-divider)]">
              {files.map((f) => (
                <FileRow
                  key={f.id}
                  file={f}
                  onSelect={(x) => setOpenId(x.id)}
                  chipLabel={f.folder ? folderLabel(f.folder) : undefined}
                  quickActions={
                    <>
                      <IconButton
                        variant="ghost"
                        aria-label={tf('actions.downloadNamed', { name: shortFileName(displayName(f)) })}
                        onClick={() => {
                          toast.info(tf('toast.downloading', { name: shortFileName(displayName(f)) }));
                          downloadFile(projectId, f).catch(fail);
                        }}
                      >
                        <icons.download size={18} aria-hidden />
                      </IconButton>
                      <ActionMenu
                        label={tf('actions.moveTo')}
                        trigger="icon"
                        variant="ghost"
                        items={[
                          { id: 'rename', label: tf('actions.rename'), run: { handler: () => setRenaming(f) } },
                          { id: 'move', label: tf('actions.move'), run: { handler: () => setMoving(f) } },
                          { id: 'replace', label: tf('actions.replace'), run: { handler: () => setReplacing(f) } },
                          { id: 'delete', label: tf('actions.delete'), tone: 'danger', run: { handler: () => setToDelete(f) } },
                        ]}
                      />
                    </>
                  }
                />
              ))}
            </div>
          )}
          {binItems.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{tf('bin.title')}</h3>
              <div className="-mx-3 flex flex-col divide-y divide-[var(--color-divider)]">
                {binItems.map((f) => (
                  <FileRow
                    key={f.id}
                    file={f}
                    chipLabel={f.folder ? folderLabel(f.folder) : undefined}
                    quickActions={
                      <Button variant="ghost" onClick={() => void restore.mutateAsync({ fileId: f.id }).then(() => toast.success(tf('toast.restored')), fail)}>
                        {tf('actions.restore')}
                      </Button>
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </SlideOver>

      {openId && (
        <ModelFileSurface
          projectId={projectId}
          model={model}
          items={files}
          openId={openId}
          onOpen={setOpenId}
          canWrite
          onRename={(f) => setRenaming(f)}
          onMove={(f) => setMoving(f)}
          onReplace={(f) => setReplacing(f)}
          onDelete={(f) => setToDelete(f)}
        />
      )}
      {uploading && (
        <UploadFilesDialog
          open
          onClose={() => setUploading(false)}
          natureLocked
          preset={{ nature: 'document' }}
          folders={[...MODEL_FOLDERS]}
          folderClass="model"
          assetFolders={[]}
          documentRoads={['folder']}
          folderLabel={folderLabel}
          projectId={projectId}
          siteId={null}
          topicOptions={[]}
          accept={ACCEPT}
          capFor={capFor}
          errorCodeOf={apiErrorCode}
          onUpload={async ({ file, answer, onProgress, signal }) => {
            await uploadFile(
              projectId,
              {
                class: 'model',
                anchor: { kind: 'model', id: model.id },
                folder: answer.folder,
                original_name: file.name,
                size_bytes: file.size,
                ...(file.type ? { declared_type: file.type } : {}),
                ...(answer.display_name ? { display_name: answer.display_name } : {}),
              },
              file,
              (p) => onProgress(p.phase === 'sizes' ? null : p.fraction),
              signal,
            );
            await qc.invalidateQueries({ queryKey: ['files', projectId] });
          }}
        />
      )}
      {renaming && (
        <RenameFileDialog
          open
          projectId={projectId}
          file={renaming}
          onClose={() => setRenaming(null)}
          // Awaited by the dialog: a refusal is its line, never a toast here.
          onRename={(file, body) => rename.mutateAsync({ fileId: file.id, ...body }).then(() => toast.success(tf('toast.renamed')))}
        />
      )}
      {moving && (
        <MoveFileDialog
          open
          file={moving}
          onClose={() => setMoving(null)}
          roads={['folder'] as MoveRoad[]}
          folders={[...MODEL_FOLDERS]}
          siteFolders={[]}
          assetFolders={[]}
          folderLabel={folderLabel}
          projectId={projectId}
          siteId={null}
          onMove={async (file, target: MoveTarget) => {
            if (target.road !== 'folder') return;
            // A refusal is the dialog's line (the code's words read there) — never caught here.
            await move.mutateAsync({ fileId: file.id, road: 'folder', folder: target.folder });
            toast.success(tf('toast.movedTo', { where: target.folder ? folderLabel(target.folder) : '' }), {
              action: { label: tf('toast.undo'), onClick: () => void move.mutateAsync({ fileId: file.id, road: 'folder', folder: file.folder }).catch(fail) },
            });
          }}
        />
      )}
      {replacing && (
        <ReplaceFileDialog
          open
          file={replacing}
          onClose={() => setReplacing(null)}
          onReplace={async (file, next) => {
            // A refusal is the dialog's line (the code's words read there) — never caught here.
            const committed = await uploadFile(projectId, {
              class: 'model',
              anchor: { kind: 'model', id: model.id },
              folder: file.folder,
              original_name: next.name,
              size_bytes: next.size,
              ...(next.type ? { declared_type: next.type } : {}),
              replaces_file_id: file.id,
            }, next);
            await qc.invalidateQueries({ queryKey: ['files', projectId] });
            toast.success(tf('toast.replaced', { version: committed.version }));
          }}
        />
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        title={toDelete ? tf('delete.title', { name: shortFileName(displayName(toDelete)) }) : ''}
        description={tf('delete.body', { days: binGraceDays })}
        confirmLabel={tf('delete.confirm')}
        tone="danger"
        onConfirm={async () => {
          const file = toDelete;
          if (!file) return;
          await del.mutateAsync({ fileId: file.id });
          setToDelete(null);
          toast.success(tf('toast.deleted', { days: binGraceDays }), { action: { label: tf('toast.undo'), onClick: () => restore.mutateAsync({ fileId: file.id }).catch(fail) } });
        }}
      />
    </>
  );
}
